import { Loader } from 'components/organization';
import { FC, FocusEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import './searchable-multiselect.scss';

export type SearchableMultiselectOption = {
  selected: boolean;
  id: number;
  value: string;
};

type SearchableMultiselectProps = {
  options: SearchableMultiselectOption[];
  onSelect: (option: SearchableMultiselectOption) => void;
  error: string;
  disabled?: boolean;
  ['data-cy']?: string;
};

export const SearchableMultiselect: FC<SearchableMultiselectProps> = ({
  options,
  onSelect,
  error,
  disabled = false,
  ...props
}) => {
  const [filteredOptions, setFilteredOptions] = useState<SearchableMultiselectOption[]>(options);
  const [filterText, setFilterText] = useState<string>('');
  const [searching, setSearching] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const warningRef = useRef<HTMLSpanElement>(null);

  const setAdornment = (visible?: 'warning') => {
    warningRef.current?.toggleAttribute('hidden', visible !== 'warning');
  };

  useEffect(() => {
    setFilteredOptions(options.filter(option => option.value.toLowerCase().startsWith(filterText.toLowerCase())));
  }, [options, filterText]);

  useEffect(() => {
    if (error) {
      setAdornment('warning');
      inputRef.current?.setCustomValidity(error);
    } else {
      setAdornment(undefined);
      inputRef.current?.setCustomValidity('');
    }
  }, [error]);

  const handleSelectOption = (option: SearchableMultiselectOption) => {
    option.selected = !option.selected;
    onSelect(option);
  };

  const handleBlur: FocusEventHandler<HTMLDivElement> = useCallback(e => {
    const checkBlurConditions = (
      parent: EventTarget & HTMLDivElement,
      child: (EventTarget & Element) | null
    ): boolean => {
      if (!child || !child.parentElement) {
        return false;
      } else if (child.parentElement === parent) {
        return true;
      }

      return checkBlurConditions(parent, child.parentElement);
    };

    const target = e.relatedTarget;
    const parent = e.currentTarget;
    if (!checkBlurConditions(parent, target)) {
      setSearching(false);
    }
  }, []);

  return (
    <div className="searchable-multiselect" onFocus={() => setSearching(true)} onBlur={handleBlur}>
      <div className="search-input-field">
        <input
          type="text"
          ref={inputRef}
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          disabled={disabled}
          data-cy={props['data-cy']}
        />
        <button
          className="btn-back clear-btn"
          type="button"
          onClick={() => {
            setFilterText('');
          }}
          disabled={disabled}
        >
          <span className="material-icons icon-large">close</span>
        </button>
        <span
          className="adornment material-icons icon-large warning"
          onMouseEnter={e => {
            if (inputRef.current && !e.currentTarget.hidden) {
              inputRef.current.reportValidity();
            }
          }}
          onClick={e => {
            if (inputRef.current && !e.currentTarget.hidden) {
              inputRef.current.reportValidity();
            }
          }}
          ref={warningRef}
          hidden
        >
          warning
        </span>
      </div>
      {searching && !disabled && (
        <div className="dropdown-container">
          <Loader loading={options.length === 0}>
            {filteredOptions.map((option, index) => (
              <div
                className="dropdown-option d-flex"
                tabIndex={index}
                key={option.value}
                onClick={() => handleSelectOption(option)}
                data-value={option.value}
              >
                <input className="mr-2" id={`locations-${index}`} type="checkbox" checked={option.selected} readOnly />
                <span>{option.value}</span>
              </div>
            ))}
          </Loader>
        </div>
      )}
    </div>
  );
};
